import { useEffect, useRef, useState } from 'react'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import {
    Alert,
    Box,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import ReadinessMeasures from '../components/report/ReadinessMeasures'
import AppFooter from '../components/layout/AppFooter'
import AppHeader from '../components/layout/AppHeader'
import Shell from '../components/layout/Shell'
import LoadingScreen from '../components/LoadingScreen'
import { getReport, getReportPdfUrl } from '../core/api/client'
import {
    classificationReason,
    sanctionDetail,
    sanctionTitle,
} from '../core/diagnostic'

const MYCIBER_URL = import.meta.env.VITE_MYCYBER_URL || 'https://myciber.gov.pt'

export default function ReportPage() {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { sessionToken } = useParams()
    const navigate = useNavigate()
    const fetched = useRef(false)
    const [report, setReport] = useState(null)
    const [error, setError] = useState('')

    useEffect(() => {
        if (fetched.current) {
            return
        }

        fetched.current = true

        async function loadReport() {
            try {
                setReport(await getReport(sessionToken))
            } catch (caught) {
                setError(caught.message)
            }
        }

        loadReport()
    }, [sessionToken])

    if (error) {
        return (
            <Shell>
                <Container maxWidth="sm" sx={{ py: 8 }}>
                    <Alert severity="error" action={<Button onClick={() => navigate('/')}>{t('report.newDiagnosis')}</Button>}>
                        {error}
                    </Alert>
                </Container>
            </Shell>
        )
    }

    if (!report) {
        return <LoadingScreen label={t('loading.report')} />
    }

    const shareUrl = window.location.href
    const showDoraAlert = report.selectedPrimarySectors?.includes('financial_market_infrastructure')
    const primaryActionHref = report.sanctionKey === 'not_applicable' ? MYCIBER_URL : getReportPdfUrl(sessionToken)
    const primaryActionLabel = report.sanctionKey === 'not_applicable'
        ? t('report.ctas.primaryLabelMyCiber')
        : t('report.ctas.primaryLabel')

    return (
        <Shell>
            <AppHeader
                action={
                    <Button startIcon={<RestartAltIcon />} variant="outlined" onClick={() => navigate('/')}>
                        {t('report.newDiagnosis')}
                    </Button>
                }
            />
            <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
                <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box>
                            <Typography variant="h2">{t('report.title')}</Typography>
                            <Typography color="text.secondary" maxWidth={760} mt={1}>
                                {classificationReason(report, t)}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <IconButton
                                aria-label={t('report.copyLabel')}
                                onClick={async () => {
                                    await navigator.clipboard.writeText(shareUrl)
                                    enqueueSnackbar(t('report.copied'), { variant: 'success' })
                                }}
                            >
                                <ContentCopyIcon />
                            </IconButton>
                        </Stack>
                    </Stack>
                    <Grid container spacing={2.5}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, height: '100%', bgcolor: 'background.paper' }}>
                                <Stack spacing={3}>
                                    <Box>
                                        <Typography color="text.secondary" mb={1}>
                                            {t('report.sanctionExposure')}
                                        </Typography>
                                        <Typography variant="h4">{sanctionTitle(report, t)}</Typography>
                                        <Stack mt={1}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t('report.sanctionDetailLabel')}
                                            </Typography>
                                            <Typography color="text.secondary">
                                                {sanctionDetail(report, t)}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                    {showDoraAlert ? <Alert severity="warning">{t('report.doraAlert')}</Alert> : null}
                                    <Alert severity="warning">{t('report.waiverNote')}</Alert>
                                    <Divider />
                                    <Stack spacing={1.2}>
                                        <Typography variant="h5">{t('report.recommendedActions')}</Typography>
                                        {
                                            report.sanctionKey === 'not_applicable'
                                                ? undefined
                                                : <Typography color="text.secondary">{t('report.ctas.urgency')}</Typography>
                                        }
                                        <Stack direction={{ xs: 'column' }} spacing={1.2}>
                                            <Button
                                                size="large"
                                                color="primary"
                                                component="a"
                                                href={primaryActionHref}
                                                target={report.sanctionKey === 'not_applicable' ? '_blank' : undefined}
                                                rel={report.sanctionKey === 'not_applicable' ? 'noreferrer' : undefined}
                                            >
                                                {primaryActionLabel}
                                            </Button>
                                            <Button
                                                size="large"
                                                component="a"
                                                color="primary"
                                                href={
                                                    'https://clml.pt/servicos/nis2/?utm_source=nis2.clml.pt&utm_medium=referral&utm_campaign=nis2form'
                                                }
                                                target={'_blank'}
                                                rel={'noreferrer'}
                                            >
                                                Fale connosco e agende uma avaliação detalhada para validar a conformidade da sua empresa.
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Grid>
                        {report.showReadiness ? (
                            <Grid size={{ xs: 12, md: 5 }}>
                                <ReadinessMeasures fishbone={report.fishbone} />
                            </Grid>
                        ) : null}
                    </Grid>
                </Stack>
            </Container>
            <AppFooter />
        </Shell>
    )
}
